import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { customerService } from '../../../services/api';
import { MenuItem, Select, InputLabel, FormControl, CircularProgress } from '@mui/material';
import {
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  Whatshot as WhatshotIcon,
  DeviceThermostat as DeviceThermostatIcon,
  AcUnit as AcUnitIcon,
  Refresh as RefreshIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { DEFAULT_FILTERS, EMPTY_FILTERS, LEAD_STATUSES, TEMPERATURE_BUCKETS } from '../utils/leadFilters';
import { useDepartments } from '../../../hooks/useDepartments';
import { TEMPERATURE_BUCKET_COLORS } from '../../../components/TemperatureChip/temperature';
import FullScreenDialog from '../../../components/FullScreenDialog';

const TEMPERATURE_BUCKET_ICONS = {
  hot: <WhatshotIcon />,
  warm: <DeviceThermostatIcon />,
  cold: <AcUnitIcon />,
};

const SectionLabel = ({ children }) => (
  <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
    {children}
  </Typography>
);

const ToggleRow = ({ label, checked, onChange }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1 }}>
    <Typography variant="body2">{label}</Typography>
    <Switch size="small" checked={checked} onChange={(e) => onChange(e.target.checked)} />
  </Box>
);

const LeadsFilterPanel = ({
  open,
  onClose,
  filters,
  onApply,
}) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(filters);

  // Buscar colaboradores
  const { data: collabData, isLoading: loadingCollabs } = useQuery({
    queryKey: ['collaborators', 'filter'],
    queryFn: () => customerService.getCollaborators({ paginationModel: { page: 0, pageSize: 100 } }),
    staleTime: 5 * 60 * 1000,
  });
  const collaborators = collabData?.data?.results ?? [];

  const { data: departments = [], isLoading: loadingDepartments } = useDepartments();

  useEffect(() => {
    if (open) setDraft(filters);
  }, [open, filters]);

  const patch = (partial) => setDraft((prev) => ({ ...prev, ...partial }));

  const toggleStatus = (status) => {
    setDraft((prev) => {
      const has = prev.status.includes(status);
      return {
        ...prev,
        status: has ? prev.status.filter((s) => s !== status) : [...prev.status, status],
      };
    });
  };

  const toggleTemperatureBucket = (bucket) => {
    setDraft((prev) => {
      const current = prev.temperatureBuckets ?? [];
      const has = current.includes(bucket);
      return {
        ...prev,
        temperatureBuckets: has ? current.filter((b) => b !== bucket) : [...current, bucket],
      };
    });
  };

  const handleClear = () => setDraft(EMPTY_FILTERS);

  const handleReset = () => setDraft(DEFAULT_FILTERS);

  const handleApply = () => {
    onApply(draft, collaborators);
    onClose();
  };

  return (
    <FullScreenDialog
      open={open}
      onClose={onClose}
      title={t('leads.filters.title')}
      actions={
        <Stack spacing={1.5} sx={{ width: '100%' }}>
          <Button
            fullWidth
            variant="text"
            size="medium"
            startIcon={<RefreshIcon fontSize="small" />}
            onClick={handleReset}
            sx={{ '& .MuiButton-startIcon': { mr: 0.75 } }}
          >
            {t('leads.filters.reset')}
          </Button>
          <Stack direction="row" spacing={1.5}>
            <Button
              fullWidth
              variant="outlined"
              size="large"
              onClick={handleClear}
            >
              {t('leads.filters.clearAll')}
            </Button>
            <Button
              fullWidth
              variant="contained"
              size="large"
              startIcon={<CheckIcon />}
              onClick={handleApply}
            >
              {t('leads.filters.apply')}
            </Button>
          </Stack>
        </Stack>
      }
    >
      <SectionLabel>{t('leads.filters.temperature')}</SectionLabel>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
        {TEMPERATURE_BUCKETS.map((bucket) => {
          const selected = (draft.temperatureBuckets ?? []).includes(bucket);
          const colorKey = TEMPERATURE_BUCKET_COLORS[bucket];
          return (
            <Chip
              key={bucket}
              icon={TEMPERATURE_BUCKET_ICONS[bucket]}
              label={t(`leads.filters.temp_${bucket}`)}
              size="medium"
              onClick={() => toggleTemperatureBucket(bucket)}
              sx={(theme) => {
                const palette = (theme.vars || theme).palette;
                const main = palette[colorKey].main;
                const tint = theme.alpha(main, 0.08);
                return {
                  height: 36,
                  bgcolor: selected ? tint : 'transparent',
                  color: selected ? main : palette.text.secondary,
                  border: '1px solid',
                  borderColor: selected ? theme.alpha(main, 0.3) : palette.divider,
                  '& .MuiChip-icon': {
                    color: selected ? main : palette.text.secondary,
                    marginLeft: '8px',
                  },
                  '&:hover': {
                    bgcolor: selected ? tint : palette.action.hover,
                  },
                };
              }}
            />
          );
        })}
      </Box>


      <Divider sx={{ my: 3 }} />

      <SectionLabel>{t('leads.filters.status')}</SectionLabel>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        {LEAD_STATUSES.map((status) => {
          const selected = draft.status.includes(status);
          return (
            <Chip
              key={status}
              label={t(`leads.filters.status_${status}`)}
              size="medium"
              onClick={() => toggleStatus(status)}
              color={selected ? 'primary' : 'default'}
              variant={selected ? 'filled' : 'outlined'}
              sx={{ height: 36 }}
            />
          );
        })}
      </Box>

      <Divider sx={{ my: 3 }} />

      <SectionLabel>{t('leads.filters.signals')}</SectionLabel>
      <ToggleRow
        label={t('leads.filters.assignedToMe')}
        checked={draft.assignedToMe}
        onChange={(v) => patch({ assignedToMe: v })}
      />
      <ToggleRow
        label={t('leads.filters.waitingForReply')}
        checked={draft.waitingForReply}
        onChange={(v) => patch({ waitingForReply: v })}
      />
      <ToggleRow
        label={t('leads.filters.isFavorite')}
        checked={draft.isFavorite}
        onChange={(v) => patch({ isFavorite: v })}
      />

      <Divider sx={{ my: 3 }} />

      <SectionLabel>{t('leads.filters.createdAt')}</SectionLabel>
      <Stack direction="row" spacing={2}>
        <TextField
          fullWidth
          size="medium"
          type="date"
          label={t('leads.filters.createdAfter')}
          InputLabelProps={{ shrink: true }}
          value={draft.createdAfter}
          onChange={(e) => patch({ createdAfter: e.target.value })}
        />
        <TextField
          fullWidth
          size="medium"
          type="date"
          label={t('leads.filters.createdBefore')}
          InputLabelProps={{ shrink: true }}
          value={draft.createdBefore}
          onChange={(e) => patch({ createdBefore: e.target.value })}
        />
      </Stack>

      <Divider sx={{ my: 3 }} />
      {/* Filtro de colaborador */}
      <SectionLabel>{t('leads.filters.collaborator')}</SectionLabel>
      <FormControl fullWidth size="medium" sx={{ mb: 2 }}>
        <InputLabel id="collaborator-select-label">
          {t('leads.filters.collaboratorSelect')}
        </InputLabel>
        <Select
          labelId="collaborator-select-label"
          value={draft.collaborator || ''}
          label={t('leads.filters.collaboratorSelect')}
          onChange={(e) => patch({ collaborator: e.target.value })}
          disabled={loadingCollabs}
          renderValue={(selected) => {
            if (!selected) return t('leads.filters.collaboratorSelect');
            const found = collaborators.find((c) => c.uuid === selected);
            return found ? found.name || found.email : selected;
          }}
        >
          <MenuItem value="">
            <em>{t('leads.filters.collaboratorSelect')}</em>
          </MenuItem>
          {loadingCollabs ? (
            <MenuItem disabled>
              <CircularProgress size={20} sx={{ mr: 1 }} /> {t('leads.loadingCollaborators')}
            </MenuItem>
          ) : (
            collaborators.map((c) => (
              <MenuItem key={c.uuid} value={c.uuid}>
                {c.name || c.email}
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>

      <Divider sx={{ my: 3 }} />
      {/* Filtro de departamento: o lead guarda o internal_name */}
      <SectionLabel>{t('leads.filters.department')}</SectionLabel>
      <FormControl fullWidth size="medium" margin="dense">
        <InputLabel id="department-select-label">
          {t('leads.filters.departmentSelect')}
        </InputLabel>
        <Select
          labelId="department-select-label"
          value={draft.department || ''}
          label={t('leads.filters.departmentSelect')}
          onChange={(e) => patch({ department: e.target.value })}
          disabled={loadingDepartments}
        >
          <MenuItem value="">
            <em>{t('leads.filters.departmentSelect')}</em>
          </MenuItem>
          {departments.map((d) => (
            <MenuItem key={d.uuid} value={d.internal_name}>
              {d.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </FullScreenDialog>
  );
};

export default LeadsFilterPanel;
